import React, { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { ConfigContext } from "../Config/ConfigContext";
import useApi from "../hooks/useApi";

/**
 * Component that initializes user verification and tenant invite acceptance.
 *
 * Flow:
 *  - Extracts the verificationToken from the URL query parameters.
 *  - Calls /auth/validate with the verification token to authenticate and set the platform cookie.
 *  - If validation succeeds and the token contains a tenantId, calls /tenant/acceptInvite/:tenantId
 *    to officially mark the tenant membership as accepted (acceptedInvite = true).
 *  - Redirects the user to the provided redirectRoute on success, or unAuthedRoute on failure.
 *
 * Notes:
 *  - The verificationToken is a JWT including tenantId in the payload when the user is
 *    following an invite flow. We decode it client-side to retrieve tenantId and then
 *    invoke the accept invite API. The server will validate access via the platform cookie.
 *
 * @component
 * @param {object} props - Component props.
 * @param {string} [props.redirectRoute="/"] - The route to redirect on valid verification.
 * @param {string} [props.unAuthedRoute="/unauthorized"] - The route to redirect on invalid verification.
 * @returns {JSX.Element} A container displaying the verification process.
 */
const UserInit = ({ redirectRoute = "/", unAuthedRoute = "/unauthorized" }) => {
	const { config } = useContext(ConfigContext); // Reserved for future configuration needs.
	const location = useLocation();
	const api = useApi();

	/**
	 * Extracts URL query parameters and returns a structured object.
	 *
	 * @param {string} search - The window.location.search string.
	 * @returns {{ verificationToken: string|null }} Object containing the verificationToken.
	 */
	const getQueryParams = (search) => {
		const params = new URLSearchParams(search);
		return {
			verificationToken: params.get("verificationToken"),
		};
	};

	/**
	 * Decodes a JWT without verifying its signature to extract the payload.
	 * This is used purely to read the tenantId for the acceptInvite call.
	 *
	 * @param {string} token - The JWT string.
	 * @returns {object|null} Decoded payload or null if decoding fails.
	 */
	const decodeJwtPayload = (token) => {
		try {
			const base64Url = token.split(".")[1];
			if (!base64Url) return null;
			const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
			const jsonPayload = decodeURIComponent(
				atob(base64)
					.split("")
					.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
					.join("")
			);
			return JSON.parse(jsonPayload);
		} catch (e) {
			console.error("Failed to decode verification token payload:", e);
			return null;
		}
	};

	const { verificationToken } = getQueryParams(location.search);

	useEffect(() => {
		(async () => {
			// If no token is present, we cannot validate the user.
			if (!verificationToken) {
				window.location.href = unAuthedRoute;
				return;
			}

			try {
				// First, validate the token. This should authenticate the user and set the platform cookie.
				const verifyResult = await api.user.validate({ verificationToken: verificationToken });

				// If not valid, redirect to unauthorized.
				if (!(verifyResult?.valid === true)) {
					window.location.href = unAuthedRoute;
					return;
				}

				// Attempt to accept the invite (if tenantId is provided in the token).
				// This marks the membership acceptedInvite = true on the backend.
				const payload = decodeJwtPayload(verificationToken);
				const tenantId = payload?.tenantId;

				if (tenantId) {
					try {
						await api.tenant.acceptInvite({ id: tenantId });
					} catch (acceptErr) {
						// If acceptance fails, log and continue; user is still validated.
						console.error("Accept invite failed:", acceptErr);
					}
				}

				// Successful validation (and invite acceptance if applicable) – redirect to the app.
				window.location.href = redirectRoute;
			} catch (err) {
				// Any error during validate should take the user to unauthorized.
				console.error("User validation failed:", err);
				window.location.href = unAuthedRoute;
			}
		})();
	}, [api, redirectRoute, unAuthedRoute, verificationToken]);

	return (
		<div className="verifyUserParetnContainer">
			<div className="optionalBreakpointContainer">
				<p className="verifyUserText">Verifying...</p>
			</div>
		</div>
	);
};

UserInit.propTypes = {
	redirectRoute: PropTypes.string,
	unAuthedRoute: PropTypes.string,
};

export default UserInit;